import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  FormControl,
  Select,
  MenuItem
} from "@mui/material";
import LoadingSkeleton from '../common/LoadingSkeleton';
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import { Description, Quiz } from "@mui/icons-material";
import ContentRatingModal from "../common/ContentRatingModal";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { learnerService } from "../../services/api";
import { useContentPlayer } from '../../context/ContentPlayerContext';
import { formatDate } from '../../utils/dateFormatter';
import EmptyState from '../common/EmptyState';
const RecentContent = () => {
  const [recentContent, setRecentContent] = useState([]);
  const [ratingModalOpen, setRatingModalOpen] = useState(false);
  const [ratingModalContentId, setRatingModalContentId] = useState(null);
  const [avgRatingMap, setAvgRatingMap] = useState({});
  const [typeFilter, setTypeFilter] = useState('all');
  const [recentLoading, setRecentLoading] = useState(false);
  const { openContentPlayer } = useContentPlayer();

  const fetchAvgForContent = async (contentId) => {
    try {
      const res = await learnerService.getContentRatings(contentId);
      if (res.data && res.data.success) {
        setAvgRatingMap((prev) => ({
          ...prev,
          [contentId]: {
            average: res.data.data.average_rating,
            total: res.data.data.total_ratings,
          },
        }));
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    const fetchRecent = async (selectedType = typeFilter) => {
      try {
        setRecentLoading(true);
        const res = await learnerService.getRecentContent(selectedType);
        const items = res.data.contents || [];
        setRecentContent(items);

        // preload averages for visible items
        if (items.length) {
          items.forEach((it) => fetchAvgForContent(it.id));
        }
      } catch (err) {
        console.error("Failed to fetch recent content", err);
      } finally {
        setRecentLoading(false);
      }
    };
    fetchRecent();
  }, [typeFilter]);

  const getContentIcon = (type) => {
    switch (type) {
      case "pdf":
        return <PictureAsPdfIcon color="primary" fontSize="large" />;
      case "video":
        return <PlayCircleFilledIcon color="primary" fontSize="large" />;
      // case "quiz":
      //   return <Quiz color="primary" fontSize="large" />;
      case "content":
      default:
        return <Description color="primary" fontSize="large" />;
    }
  };

  const openRatingModal = (contentId) => {
    setRatingModalContentId(contentId);
    setRatingModalOpen(true);
  };

  const closeRatingModal = () => {
    setRatingModalOpen(false);
    setRatingModalContentId(null);
  };
  const handleContentClick = async (contentId, courseId) => {
    openContentPlayer(
      courseId || null, // courseId (can be null if not part of course)
      contentId,        // contentId
      courseId ? `/courses/${courseId}` : '/', // returnUrl
      null,             // refreshOnComplete
      true,             // isLearnerMode
      null, null, null, null, null, null, null, false,
      true              // byContentId: the content's own ID, so the course's module that holds it opens (N40.1 pt 11)
    );
  }

  return (
    <Box sx={{ width: '100%', py: 2 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 1.5,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" gutterBottom>
            Recent Content
          </Typography>
        </Box>

        <FormControl
          sx={{
            minWidth: 180,
            maxWidth: 320,
            '& .MuiInputBase-root': {
              padding: '0 8px',
              fontSize: '14px',
            },
            '& .MuiSelect-select': {
              padding: '8px 10px',
            },
          }}
        >
          <Select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            displayEmpty
            size="small"
          >
            <MenuItem value="all">All Types</MenuItem>
            <MenuItem value="video">Video</MenuItem>
            {/* <MenuItem value="pdf">PDF / Document</MenuItem> */}
            {/* <MenuItem value="quiz">Quiz</MenuItem> */}
            <MenuItem value="content">Content</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* CARDS: below the top row */}
      {recentLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <LoadingSkeleton rows={3} />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {recentContent.length === 0 && (
            <Grid item xs={12}>
              {/* The shared empty state with this screen's own words (K2.3 pt 14). Removed 23 Oct 2026 (K2.3 pt 14): a plain line. */}
              <EmptyState title="No recent content found." message={null} />
            </Grid>
          )}

          {recentContent.map((item) => {
            const avgData = avgRatingMap[item.id] || { average: 0, total: 0 };
            return (
              <Grid item xs={12} sm={6} md={4} key={item.id}>
                <Card
                  variant="outlined"
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    borderRadius: 2,
                    '&:hover': { boxShadow: 3 },
                    minHeight: 160,
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                    <Box display="flex" alignItems="flex-start" gap={2}>
                      <Box sx={{ mr: 1 }}>
                        {getContentIcon(item.type)}
                      </Box>

                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ cursor: 'pointer' }} onClick={() => handleContentClick(item.id, item.course_id)}>
                          {item.name}
                        </Typography>

                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            mt: 0.5,
                            display: 'block',
                          }}
                        >
                          {item.description
                            ? item.description.replace(/<\/?p>/g, '').replace(/<[^>]*>/g, '')
                            : ''}
                        </Typography>

                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                          Accessed on{' '}
                          {item.accessedAt ? formatDate(item.accessedAt) : 'N/A'}
                        </Typography>

                        <Box mt={1}>
                          <Chip label={item.type || 'content'} size="small" />
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>

                  <Box
                    onClick={() => openRatingModal(item.id)}
                    sx={{
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      px: 2,
                      pb: 1.5,
                      pt: 0.5,
                      gap: 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                      {[1, 2, 3, 4, 5].map((i) =>
                        i <= Math.round(avgData.average) ? (
                          <StarIcon key={i} color="warning" fontSize="small" />
                        ) : (
                          <StarBorderIcon key={i} fontSize="small" />
                        )
                      )}
                    </Box>

                    <Box sx={{ fontSize: '0.95rem', color: 'text.primary' }}>
                      {avgData.average || 0} ({avgData.total})
                    </Box>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <ContentRatingModal
        open={ratingModalOpen}
        onClose={closeRatingModal}
        contentId={ratingModalContentId}
        onSubmitted={() => {
          if (ratingModalContentId) fetchAvgForContent(ratingModalContentId);
        }}
      />
    </Box>
  );
};

export default RecentContent;
